<template>
  <v-card class="full-height card-container">
    <!-- App Bar -->
    <div class="calendar-app-bar" :class="{ 'drawer-open': drawer }">
      <v-btn icon @click="drawer = !drawer" class="mr-2" depressed>
        <v-icon>mdi-menu</v-icon>
      </v-btn>

      <v-btn
        icon
        @click="previousPeriod"
        class="ma-1"
        color="primary"
        small
        depressed
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <v-btn
        color="primary"
        outlined
        @click="setToday"
        class="mx-2"
        small
        depressed
      >
        Hoje
      </v-btn>

      <v-btn
        icon
        @click="nextPeriod"
        class="ma-1"
        color="primary"
        small
        depressed
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
      <v-spacer></v-spacer>

      <v-btn-toggle
        v-model="type"
        color="primary"
        class="mr-4"
        style="border-radius: 10px"
      >
        <v-btn height="40" value="month" small depressed> Mês </v-btn>
        <v-btn height="40" value="week" small depressed> Semana </v-btn>
        <v-btn height="40" value="day" small depressed> Dia </v-btn>
      </v-btn-toggle>

      <v-btn
        class="primary-button ma-1"
        @click="showPatternRequestDialog = true"
        depressed
      >
        Solicitação de Padrão
      </v-btn>

      <v-btn
        class="primary-button ma-1"
        @click="showEventDialog = true"
        depressed
      >
        <v-icon left>mdi-plus</v-icon>
        Novo agendamento
      </v-btn>
    </div>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      absolute
      width="300"
      class="calendar-sidebar"
    >
      <!-- Create Button -->
      <div class="pa-4 py-8 d-flex justify-center align-center">
        <div class="main-toolbar d-flex align-center">
          <div class="icon-back" style="width: 45px; height: 45px">
            <v-icon color="white" size="20">mdi-calendar-clock</v-icon>
          </div>

          <span class="main-toolbar-title mt-2" style="font-size: 16px">
            Central de Equipamentos
          </span>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Mini Calendar -->
      <div class="pa-4">
        <div class="mini-calendar-container">
          <!-- Calendar Header -->
          <div
            class="mini-calendar-header d-flex align-center justify-space-between mb-2"
          >
            <v-btn icon small @click="previousMonth" class="calendar-nav-btn">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>

            <date-picker
              v-model="miniCalendarPickerDate"
              mode="date"
              locale="pt-BR"
              :popover="{ visibility: 'click', positionFixed: true }"
              @input="navigateToSelectedDate"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div 
                  class="mini-calendar-title text-subtitle-2 font-weight-medium"
                  v-on="inputEvents"
                >
                  {{ currentMonthYear }}
                </div>
              </template>
            </date-picker>

            <v-btn icon small @click="nextMonth" class="calendar-nav-btn">
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <!-- Calendar Grid -->
          <div class="mini-calendar-grid">
            <!-- Weekday Headers -->
            <div class="mini-calendar-weekdays">
              <div
                v-for="(day, index) in weekDays"
                :key="index"
                class="mini-calendar-weekday"
              >
                {{ day }}
              </div>
            </div>

            <!-- Calendar Days -->
            <div class="mini-calendar-days">
              <div
                v-for="(day, index) in calendarDays"
                :key="index"
                class="mini-calendar-day"
                :class="{
                  'other-month': day.otherMonth,
                  today: day.isToday,
                  selected: day.isSelected,
                  'has-events': day.hasEvents,
                }"
                @click="selectDate(day)"
              >
                <span class="day-number">{{ day.day }}</span>
                <div v-if="day.hasEvents" class="event-indicators">
                  <div
                    v-for="(event, eventIndex) in day.events.slice(0, 2)"
                    :key="eventIndex"
                    class="event-dot"
                    :style="{ backgroundColor: event.color }"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          v-bind="attrs"
                          v-on="on"
                          class="event-dot-inner"
                        ></div>
                      </template>
                      <span>
                        <strong>{{
                          event.name || event.calibrationPatternName
                        }}</strong>
                        <br v-if="event.startHour && event.endHour" />
                        <span v-if="event.startHour && event.endHour">
                          {{ event.startHour }} - {{ event.endHour }}
                        </span>
                        <br v-if="event.destinationInstitutionName" />
                        <span v-if="event.destinationInstitutionName">
                          {{ event.destinationInstitutionName }}
                        </span>
                      </span>
                    </v-tooltip>
                  </div>
                  <div v-if="day.events.length > 2" class="more-events">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          +{{ day.events.length - 2 }}
                        </span>
                      </template>
                      <span>
                        <div
                          v-for="(event, idx) in day.events.slice(2)"
                          :key="idx"
                        >
                          {{ event.name || event.calibrationPatternName }}
                        </div>
                      </span>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Padrões Section -->
      <div class="patterns-section">
        <div class="pa-4 pb-2">
          <div class="text-subtitle-1 font-weight-medium mb-3">
            Padrões
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-2" v-bind="attrs" v-on="on"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span
                >Arraste um padrão para o calendário para abrir o formulário de
                agendamento</span
              >
            </v-tooltip>
          </div>
        </div>
        <div class="d-flex align-center justify-center pt-0 pb-2">
          <!-- Campo de busca continua igual -->
          <v-text-field
            v-model="searchPattern"
            label="Pesquisar padrões"
            class="primary-input ml-4 mb-0 pa-0"
            outlined
            dense
            hide-details
            @keyup.enter="applyPatternSearchFilter"
            clearable
            @click:clear="clearPatternSearch"
            ><template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">
                    mdi-progress-question
                  </v-icon>
                </template>
                <span>Buscar por nome, número de série ou patrimônio</span>
              </v-tooltip>
            </template></v-text-field
          >

          <!-- Menu popup ao clicar no botão -->
          <v-menu
            v-model="showFilter"
            :close-on-content-click="false"
            offset-y
            transition="scale-transition"
            max-width="700"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="primary-button ml-2"
                :color="isFilterActive ? 'secondary-button' : 'primary-button'"
                small
                style="height: 40px; min-width: 40px"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-filter</v-icon>
              </v-btn>
            </template>

            <v-card class="pa-4" style="max-width: 500px">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Instituição') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="institutionId"
                    :items="institutions"
                    @change="getUnidades"
                    item-value="id"
                    item-text="name"
                    outlined
                    class="primary-input"
                    append-icon=""
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Unidade') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="unidadeId"
                    :items="unidades"
                    item-value="id"
                    item-text="name"
                    :disabled="!institutionId"
                    :placeholder="
                      !institutionId ? $t('Selecione uma instituição') : ''
                    "
                    outlined
                    class="primary-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Modelo') }}</label>
                  <v-text-field
                    hide-details="auto"
                    v-model="patternModel"
                    outlined
                    class="primary-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Fabricante') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="patternManufacturer"
                    :items="manufacturers"
                    item-text="name"
                    item-value="name"
                    outlined
                    append-icon="mdi-chevron-down"
                    class="primary-input"
                    @keyup.enter="filterTable"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12">
                  <label class="label">{{ $t('Alienado') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="patternAlienated"
                    :items="alienatedOptions"
                    item-text="text"
                    item-value="value"
                    :placeholder="$t('Não Alienados')"
                    outlined
                    class="primary-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" class="d-flex justify-end align-end">
                  <v-btn
                    class="mx-2 secondary-button"
                    color="#BBCF20"
                    @click="filterTable"
                    depressed
                  >
                    <span>{{ $t('Filtrar') }}</span>
                    <v-icon class="pl-2">mdi-magnify</v-icon>
                  </v-btn>

                  <v-btn
                    depressed
                    color="#20202066"
                    class="clear-button"
                    @click="reset"
                  >
                    <span>{{ $t('Limpar') }}</span>
                    <v-icon class="pl-2">mdi-cached</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-menu>
        </div>

        <div class="patterns-list-container">
          <v-list dense class="patterns-list">
            <v-list-item
              v-for="pattern in calibrationV2Patterns"
              :key="pattern.id"
              @click="() => selectPattern(pattern)"
              class="px-4 pattern-item"
              :class="{
                'pattern-selected': selectedPattern?.id === pattern.id,
              }"
              draggable="true"
              @dragstart="onPatternDragStart($event, pattern)"
              @dragend="onPatternDragEnd"
            >
              <v-tooltip
                top
                color="white"
                content-class="custom-tooltip"
                open-delay="200"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    style="width: 100%; display: flex"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        <v-icon
                          v-if="pattern.borrowedOnInstitutionId != null"
                          small
                          color="orange"
                          class="ml-2"
                        >
                          mdi-tray-arrow-up
                        </v-icon>
                        <v-icon v-else small color="green" class="ml-2">
                          mdi-check-circle
                        </v-icon>
                        {{ pattern.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ pattern.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon small class="drag-handle">mdi-drag</v-icon>
                    </v-list-item-action>
                  </div>
                </template>
                <span>
                  <strong>{{ pattern.name }}</strong
                  ><br />
                  <span v-if="pattern.description">{{
                    pattern.description
                  }}</span>
                  <span v-if="pattern.model"
                    >Modelo: {{ pattern.model }}<br
                  /></span>
                  <span v-if="pattern.manufacturer"
                    >Fabricante: {{ pattern.manufacturer }}<br
                  /></span>
                  <span v-if="pattern.serialNumber"
                    >Nº Série: {{ pattern.serialNumber }}<br
                  /></span>
                  <span v-if="pattern.propertyCode"
                    >Patrimônio: {{ pattern.propertyCode }}<br
                  /></span>
                  <span v-if="pattern.borrowedOnInstitution?.name"
                    >Emprestado para: {{ pattern.borrowedOnInstitution?.name
                    }}<br
                  /></span>
                </span>
              </v-tooltip>
            </v-list-item>
          </v-list>
        </div>

        <v-btn
          class="secondary-button ma-2 mt-2"
          @click="showEventDialog = true"
          depressed
          style="margin-top: auto; flex-shrink: 0;"
        >
          <v-icon left>mdi-plus</v-icon>
          Novo agendamento
        </v-btn>
      </div>
    </v-navigation-drawer>

    <!-- Main Content Area -->
    <div class="calendar-main-content" :class="{ 'drawer-open': drawer }">
      <!-- Title Bar -->
      <div class="calendar-title-bar text-center pa-4">
        <div class="text-h5 font-weight-medium">
          {{ title }}
        </div>
      </div>

      <!-- Main Calendar -->
      <div
        class="calendar-drop-zone"
        @dragover="onCalendarDragOver"
        @drop="onCalendarDrop"
        @dragleave="onCalendarDragLeave"
        :class="{ 'drag-over': dragOverDate }"
      >
        <v-calendar
          ref="calendar"
          v-model="focus"
          color="primary"
          :events="visibleEvents"
          :event-margin-bottom="3"
          :event-color="getEventColor"
          :event-ripple="false"
          :type="type"
          @click:event="showEvent"
          @click:more="viewDay"
          @click:date="clickDate"
          @change="updateRange"
          @mousedown:event="startDrag"
          @mousedown:time="startTime"
          @mousemove:time="mouseMove"
          @mouseup:time="endDrag"
          @mouseleave.native="cancelDrag"
          class="calendar-main"
          locale="pt-br"
        >
          <template v-slot:event="{ event, timed, eventSummary }">
            <div class="v-event-draggable">
              <component :is="{ render: eventSummary }"></component>
            </div>
            <div
              v-if="timed"
              class="v-event-drag-bottom"
              @mousedown.stop="extendBottom(event)"
            ></div>
          </template>
        </v-calendar>
      </div>
    </div>

    <!-- Event Details Dialog -->
    <EventDetailsDialog
      v-model="selectedOpen"
      :event="selectedEvent"
      @edit-event="editEvent"
      @event-deleted="removeScheduling"
    />

    <!-- Create/Edit Event Dialog -->
    <EventFormDialog
      v-model="showEventDialog"
      :editing-event="editingEvent"
      :event-data="eventForm"
      :institution="institution"
      :event-colors="eventColors"
      :suppliers="suppliers"
      @save-event="handleSaveEvent"
      @institution-selected="getUnidades"
      @event-saved="reloadScheduling"
    />

    <v-dialog v-model="showPatternRequestDialog" max-width="1000">
      <RequestModal @close="showPatternRequestDialog = false" />
    </v-dialog>
  </v-card>
</template>

<script>
import EventDetailsDialog from './components/EventDetailsDialog.vue'
import EventFormDialog from './components/EventFormDialog.vue'
import RequestModal from './../CalibrationV2/components/pattern/requestsModal.vue'
import ApiService from '@/services/ApiService.js'

export default {
  name: 'EquipmentCentral',
  components: {
    EventDetailsDialog,
    EventFormDialog,
    RequestModal,
  },
  data() {
    return {
      apiService: new ApiService(),
      institution: null,
      drawer: true,
      focus: '',
      type: 'month',
      miniCalendarValue: '',
      miniCalendarDate: new Date(),
      selectedMiniDate: new Date(),
      weekDays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      typeToLabel: {
        month: 'Mês',
        week: 'Semana',
        day: 'Dia',
      },
      start: null,
      end: null,
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      showEventDialog: false,
      editingEvent: false,
      eventForm: {
        name: '',
        startDateTime: '',
        endDateTime: '',
        calibrationPattern: null,
        destinationInstitution: null,
        destinationUnidade: null,
        color: '#2196f3',
        details: '',
        supplierId: null,
      },
      eventColors: [
        { name: 'Empréstimo', value: '#2196f3' },
        { name: 'Translado', value: '#ff9800' },
        { name: 'Calibração', value: '#5b5b5b' },
        ,
      ],
      selectedPatterns: [],
      calibrationV2Patterns: [],
      institutions: [],
      unidades: [],
      events: [],
      miniCalendarEvents: [],
      draggedPattern: null,
      dragOverDate: null,
      dragEvent: null,
      dragTime: null,
      createEvent: null,
      createStart: null,
      extendOriginal: null,
      searchPattern: '',
      showFilter: false,
      filter: {
        institutionId: null,
        unidadeId: null,
        patternModel: null,
        patternManufacturer: null,
        patternAlienated: false,
      },
      unidadeId: null,
      patternModel: null,
      patternManufacturer: null,
      patternAlienated: false,
      alienatedOptions: [
        { text: 'Todos', value: null },
        { text: 'Alienados', value: true },
        { text: 'Não alienados', value: false },
      ],
      manufacturers: [],
      institutionId: null,
      user: [],
      showPatternRequestDialog: false,
      loading: false,
      selectedPattern: null,
      allEvents: [],
      filteredEvents: [],
      suppliers: [],
      miniCalendarPickerDate: new Date(),
      activeSearchFilter: null,
    }
  },
  created() {
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.user = JSON.parse(localStorage.getItem('user'))
    this.getScheduling()
    this.getMiniCalendarScheduling()
    this.getManufacturers()
    this.getFilteredPatterns()
    this.getSupplier()
  },

  computed: {
    title() {
      try {
        if (!this.start || !this.end) return '-'

        const start = this.start
        const end = this.end

        const startMonth = this.monthFormatter(start)
        const endMonth = this.monthFormatter(end)

        const startYear = start.year
        const endYear = end.year

        const startDay = start.day
        const endDay = end.day

        const sameMonth = startMonth === endMonth && startYear === endYear

        if (this.type === 'month') {
          // mês sempre pelo início do range
          return `${startMonth} ${startYear}`
        }

        if (this.type === 'week' || this.type === '4day') {
          if (sameMonth) {
            return `${startDay}–${endDay} ${startMonth} ${startYear}`
          }

          return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`
        }

        if (this.type === 'day') {
          return `${startDay} ${startMonth} ${startYear}`
        }

        return '-'
      } catch (error) {
        console.error('Error in title computed:', error)
        return '-'
      }
    },
    visibleEvents() {
      const events = this.events.map((event) => ({
        ...event,
        start: event.start,
        end: event.end,
        timed: event.timed !== false,
      }))

      if (this.createEvent) {
        events.push(this.createEvent)
      }
      return events
    },
    currentMonthYear() {
      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        console.error(
          'Data inválida do miniCalendário em currentMonthYear:',
          this.miniCalendarDate
        )
        return new Date().toLocaleDateString('pt-BR', {
          month: 'long',
          year: 'numeric',
        })
      }
      return this.miniCalendarDate.toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric',
      })
    },
    calendarDays() {
      let year, month

      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        console.error(
          'Data inválida do miniCalendário em calendarDays:',
          this.miniCalendarDate
        )
        // Padrão para o mês atual
        const today = new Date()
        year = today.getFullYear()
        month = today.getMonth()
      } else {
        year = this.miniCalendarDate.getFullYear()
        month = this.miniCalendarDate.getMonth()
      }

      // First day of the month
      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)

      // Start from Sunday of the week containing the first day
      const startDate = new Date(firstDay)
      startDate.setDate(startDate.getDate() - startDate.getDay())

      // End on Saturday of the week containing the last day
      const endDate = new Date(lastDay)
      endDate.setDate(endDate.getDate() + (6 - endDate.getDay()))

      const days = []
      const currentDate = new Date(startDate)
      const today = new Date()

      while (currentDate <= endDate) {
        const dayEvents = this.getEventsForDate(currentDate, 'mini')
        const isCurrentMonth = currentDate.getMonth() === month
        const isToday = this.isSameDate(currentDate, today)
        const isSelected = this.isSameDate(currentDate, this.selectedMiniDate)

        // Create a new date object to avoid reference issues
        const dayDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate()
        )

        days.push({
          day: currentDate.getDate(),
          date: dayDate,
          otherMonth: !isCurrentMonth,
          isToday,
          isSelected,
          hasEvents: dayEvents.length > 0,
          events: dayEvents,
        })

        currentDate.setDate(currentDate.getDate() + 1)
      }

      return days
    },
    isFilterActive() {
      return (
        this.filter.institutionId !== null ||
        this.filter.unidadeId !== null ||
        !!this.filter.patternModel ||
        !!this.filter.patternManufacturer
      )
    },
  },

  mounted() {
    // Inicializar calendário com a data atual na visualização de mês
    const today = new Date()
    this.focus = this.getDateString(today)
    this.type = 'month'

    this.$refs.calendar.checkChange()

    if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
      this.miniCalendarDate = new Date()
    }
    if (!this.selectedMiniDate || isNaN(this.selectedMiniDate.getTime())) {
      this.selectedMiniDate = new Date()
    }
  },

  methods: {
    async reloadScheduling() {
      // Se o calendário tem um range visível (start/end), recarrega com essas datas
      if (this.start && this.end) {
        const startDate = new Date(`${this.start.date}T00:00:00`)
        const endDate = new Date(`${this.end.date}T23:59:59`)
        await this.getScheduling(startDate, endDate)
      } else {
        // Caso contrário, recarrega o mês atual
        await this.getScheduling()
      }
      await this.getMiniCalendarScheduling()
    },

    async getSupplier() {
      await this.apiService
        .get(`supplier/list/${this.institution.id}`)
        .then((response) => {
          this.suppliers = response.content
        })
        .catch((error) => {
          console.error('Erro ao obter fornecedores', error)
          this.$toast.error('Erro ao obter fornecedores')
        })
    },

    async getScheduling(startDate = null, endDate = null) {
      const now = new Date()

      if (!startDate || !endDate) {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        endDate = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        )
      }

      const filter = {
        institutionId: this.institution.id,
        startDate: this.getDateString(startDate),
        endDate: this.getDateString(endDate),
      }

      await this.apiService
        .post(`scheduling/list-by-instituitionid`, filter)
        .then((response) => {
          const parsedEvents = response.content.map((event) => ({
            ...event,
            name: event.schedulingPatterns
              ? event.schedulingPatterns
                  .map((x) => {
                    const propertyCode = x.pattern?.propertyCode || 'S/P'
                    const patternName = x.pattern?.name || ''
                    const institution = event.toInstitution?.name || ''
                    return `${propertyCode} - ${patternName} | ${institution}`
                  })
                  .join(', ')
              : '',
            start: new Date(event.startDate).getTime(),
            end: new Date(event.endDate).getTime(),
            timed:false,
          }))

          this.allEvents = parsedEvents
          this.events = [...parsedEvents]

          this.filterEventsByPattern()
        })
    },

    filterEventsByPattern() {
      let filteredEvents = [...this.allEvents]

      // Filtro por padrão selecionado na lista
      if (this.selectedPattern) {
        filteredEvents = filteredEvents.filter((event) =>
          event.schedulingPatterns?.some(
            (p) => p.patternId === this.selectedPattern.id
          )
        )
      }

      // Filtro por busca de texto (nome, nº série ou patrimônio)
      if (this.activeSearchFilter) {
        const searchLower = this.activeSearchFilter.toLowerCase()
        filteredEvents = filteredEvents.filter((event) => {
          return event.schedulingPatterns?.some((sp) => {
            const pattern = sp.pattern
            if (!pattern) return false
            
            const matchName = pattern.name?.toLowerCase().includes(searchLower)
            const matchSerial = pattern.serialNumber?.toLowerCase().includes(searchLower)
            const matchProperty = pattern.propertyCode?.toLowerCase().includes(searchLower)
            
            return matchName || matchSerial || matchProperty
          })
        })
      }

      this.events = filteredEvents
    },

    async getMiniCalendarScheduling(year = null, month = null) {
      // Se não foram passados ano/mês, usa a data do miniCalendar
      if (year === null || month === null) {
        year = this.miniCalendarDate.getFullYear()
        month = this.miniCalendarDate.getMonth()
      }

      const startDate = new Date(year, month, 1)
      const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999)

      const filter = {
        institutionId: this.institution.id,
        startDate: this.getDateString(startDate),
        endDate: this.getDateString(endDate),
      }

      try {
        const response = await this.apiService.post(
          `scheduling/list-by-instituitionid-mini-calendar`,
          filter
        )
        const miniEvents = response.content.map((event) => ({
          ...event,
          name: event.pattern ? event.pattern.name : '',
          start: new Date(event.startDate).getTime(),
          end: new Date(event.endDate).getTime(),
          timed: event.timed !== false,
        }))

        this.miniCalendarEvents = miniEvents
      } catch (error) {
        this.$toast.error('Erro ao obter agendamentos do mini calendário')
      }
    },

    async handleSaveEvent({ eventForm, editingEvent }) {
      const timezoneHeader = {
        headers: {
          institutiontimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }

      try {
        const schedulingPatterns =
          eventForm.calibrationPattern?.length > 0
            ? eventForm.calibrationPattern
            : this.selectedPatterns?.map((p) => p.id) ?? []

        if (schedulingPatterns.length === 0) {
          this.$toast.error('Selecione pelo menos um padrão para agendar')
          return
        }

        if (editingEvent && eventForm.id) {
          const schedulingEdit = {
            id: eventForm.id,
            fromInstitutionId: this.institution.id,
            toInstitutionId: eventForm.destinationInstitution,
            toUnidadeId: eventForm.destinationUnidade,
            alertDate: new Date(eventForm.alertDateTime),
            startDate: new Date(eventForm.startDateTime),
            endDate: new Date(eventForm.endDateTime),
            color: eventForm.color,
            details: eventForm.details,
            supplierId: eventForm.supplierId,
            patternIds: schedulingPatterns,
          }

          await this.apiService.put(
            'scheduling/edit',
            schedulingEdit,
            timezoneHeader
          )
          this.$toast.success('Agendamento atualizado com sucesso!')
        } else {
          const schedulingAdd = {
            institutionId: this.institution.id,
            toUnidadeId: eventForm.destinationUnidade,
            fromInstitutionId: this.institution.id,
            toInstitutionId: eventForm.destinationInstitution,
            userId: this.user.id,
            startDate: new Date(eventForm.startDateTime),
            endDate: new Date(eventForm.endDateTime),
            alertDate: new Date(eventForm.alertDateTime),
            color: eventForm.color,
            details: eventForm.details,
            supplierId: eventForm.supplierId,
            patternIds: schedulingPatterns,
          }

          await this.apiService.post(
            'scheduling/add',
            schedulingAdd,
            timezoneHeader
          )
          this.$toast.success('Agendamento criado com sucesso!')
        }
        this.showEventDialog = false

        this.getScheduling()
        this.getMiniCalendarScheduling()
        this.editingEvent = false
        this.selectedPatterns = []
      } catch (error) {
        this.$toast.error
      }
    },
    getManufacturers() {
      this.manufacturerLoading = true
      this.apiService
        .get(`manufacturer/list/${this.institution.id}`)
        .then((response) => {
          this.manufacturers = response.content
          this.manufacturerLoading = false
        })
        .catch(() => {
          this.manufacturerLoading = false
        })
    },
    removeScheduling(event) {
      this.apiService
        .delete(`scheduling/delete/${event.id}`)
        .then(() => {
          this.$toast.success('Agendamento removido com sucesso!')
          this.getScheduling()
          this.getMiniCalendarScheduling()
        })
        .catch((error) => {
          console.error('Erro ao remover agendamento', error)
          this.$toast.error('Erro ao remover agendamento')
        })
    },
    async getUnidades(institutionId) {
      await this.apiService
        .get(`unidade/list/${institutionId}`)
        .then((response) => {
          this.unidades = response.content
        })
    },
    getFilteredPatterns() {
      if (!this.searchPattern) {
        this.getPattern()
        return
      }

      this.apiService
        .get(`calibration/v2/pattern/scheduling-requests`, {
          institutionId: this.institution.id,
          Search: this.searchPattern,
          Page: 1,
          PageSize: 10,
        })
        .then((response) => {
          this.calibrationV2Patterns = response.content.data
        })
        .catch((error) => {
          console.error('Erro ao buscar padrões', error)
          this.$toast.error('Erro ao buscar padrões')
        })
    },
    async getPattern() {
      await this.apiService
        .get(`calibration/v2/pattern/list/${this.institution.id}`)
        .then((response) => {
          this.calibrationV2Patterns = response.content
        })
    },
    filterTable() {
      this.page = 1
      this.filter.page = this.page
      this.filter.pageSize = this.pageSize
      this.filter.manufacturer = this.patternManufacturer
      this.filter.patternModel = this.patternModel
      this.filter.alienated = this.patternAlienated
      this.list()
    },
    async list() {
      this.filter.institutionId = this.institution.Id
      this.filter.unidadeId = this.unidadeId

      await this.apiService
        .post('calibration/v2/pattern/list', this.filter)
        .then((response) => {
          this.calibrationV2Patterns = response.content.data
        })
        .finally(() => {
          this.showFilter = false
        })
    },
    reset() {
      this.filter = {
        institutionId: this.institution.Id,
        unidadeId: null,
        propertyCode: null,
        manufacturer: null,
        serialNumber: null,
        model: null,
        alienated: null,
      }
      this.institutionId = null
      this.unidadeId = null
      this.patternManufacturer = null
      this.patternModel = null
      this.patternAlienated = null

      this.list()
    },
    viewDay({ date }) {
      this.focus = date
      this.type = 'day'
    },

    getEventColor(event) {
      const rgb = parseInt(event.color.substring(1), 16)
      const r = (rgb >> 16) & 0xff
      const g = (rgb >> 8) & 0xff
      const b = (rgb >> 0) & 0xff

      return event === this.dragEvent
        ? `rgba(${r}, ${g}, ${b}, 0.7)`
        : event === this.createEvent
        ? `rgba(${r}, ${g}, ${b}, 0.7)`
        : event.color
    },

    setToday() {
      const today = new Date()
      this.focus = this.getDateString(today)
    },

    nth(d) {
      return d > 3 && d < 21
        ? 'th'
        : ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'][d % 10]
    },

    monthFormatter(date) {
      try {
        if (!date || (!date.year && !date.month)) {
          console.error('Objeto de data inválido em monthFormatter:', date)
          return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
            new Date()
          )
        }

        // Validate year and month values
        if (
          isNaN(date.year) ||
          isNaN(date.month) ||
          date.month < 1 ||
          date.month > 12
        ) {
          console.error('Ano ou mês inválido em monthFormatter:', date)
          return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
            new Date()
          )
        }

        return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
          new Date(date.year, date.month - 1)
        )
      } catch (error) {
        console.error('Erro em monthFormatter:', error, date)
        return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
          new Date()
        )
      }
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        )
      }

      if (this.selectedOpen) {
        this.selectedOpen = false
        requestAnimationFrame(() => requestAnimationFrame(() => open()))
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    },

    updateRange({ start, end }) {
      // guarda o range visível para o título
      this.start = start
      this.end = end

      // monta datas JS para buscar os eventos
      const startDate = new Date(`${start.date}T00:00:00`)
      const endDate = new Date(`${end.date}T23:59:59`)

      this.getScheduling(startDate, endDate)
    },

    editEvent(event) {
      this.selectedOpen = false
      this.editingEvent = true

      this.eventForm = {
        id: event.id,
        calibrationPattern:
          event.schedulingPatterns?.map((p) => p.patternId) || [],
        destinationInstitution: event.toInstitutionId,
        destinationUnidade: event.toUnidadeId,
        startDateTime: new Date(event.startDate),
        endDateTime: new Date(event.endDate),
        color: event.color || '#2196f3',
        details: event.details || '',
        supplierId: event.supplierId || null,
        alert: !!event.alertDate,
        alertDateTime: event.alertDate ? new Date(event.alertDate) : null,
      }

      this.showEventDialog = true
    },

    getDateString(date) {
      if (!date || isNaN(date.getTime())) {
        console.error('Invalid date passed to getDateString:', date)
        return new Date().toISOString().substr(0, 10) // Return today's date as fallback
      }
      return date.toISOString().substr(0, 10)
    },

    onMiniCalendarClick({ date }) {
      this.focus = date
      this.type = 'day'
    },

    hasEventsOnDate(date) {
      return this.visibleEvents.some((event) => event.start === date)
    },

    selectPattern(pattern) {
      const isSelected = this.selectedPattern?.id === pattern.id

      this.selectedPattern = isSelected ? null : pattern

      this.filterEventsByPattern()
    },

    applyPatternSearchFilter() {
      if (!this.searchPattern || this.searchPattern.trim() === '') {
        this.clearPatternSearch()
        return
      }

      this.activeSearchFilter = this.searchPattern.trim()
      this.getFilteredPatterns()
      this.filterEventsByPattern()
      
      const matchCount = this.events.length
      this.$toast.info(`${matchCount} agendamento(s) encontrado(s)`)
    },

    clearPatternSearch() {
      this.searchPattern = ''
      this.activeSearchFilter = null
      this.getPattern()
      this.filterEventsByPattern()
    },

    // Métodos do Mini Calendário
    previousMonth() {
      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        this.miniCalendarDate = new Date()
        return
      }
      const newDate = new Date(this.miniCalendarDate)
      newDate.setMonth(newDate.getMonth() - 1)
      this.miniCalendarDate = newDate

      this.getMiniCalendarScheduling(newDate.getFullYear(), newDate.getMonth())
    },

    nextMonth() {
      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        this.miniCalendarDate = new Date()
        return
      }
      const newDate = new Date(this.miniCalendarDate)
      newDate.setMonth(newDate.getMonth() + 1)
      this.miniCalendarDate = newDate

      this.getMiniCalendarScheduling(newDate.getFullYear(), newDate.getMonth())
    },

    selectDate(day) {
      if (!day || !day.date || isNaN(day.date.getTime())) {
        console.error('Data inválida selecionada:', day)
        return
      }

      try {
        if (!day.otherMonth) {
          this.selectedMiniDate = new Date(day.date)
          // Navigate main calendar to selected date
          const dateString = this.getDateString(day.date)
          this.focus = dateString
          this.type = 'day'

          // Show info about events on this day
          if (day.hasEvents && day.events.length > 0) {
            const eventNames = day.events
              .map((e) => e.name || e.calibrationPatternName)
              .join(', ')
            this.$toast.info(
              `${day.events.length} evento(s) em ${day.day}: ${eventNames}`
            )
          }
        } else {
          // If clicking on other month day, navigate to that month
          this.miniCalendarDate = new Date(day.date)
          this.selectedMiniDate = new Date(day.date)
          const dateString = this.getDateString(day.date)
          this.focus = dateString
          this.type = 'day'
        }
      } catch (error) {
        console.error('Error in selectDate:', error, day)
      }
    },

    isSameDate(date1, date2) {
      if (
        !date1 ||
        !date2 ||
        isNaN(date1.getTime()) ||
        isNaN(date2.getTime())
      ) {
        return false
      }
      return date1.toDateString() === date2.toDateString()
    },

    getEventsForDate(date, source = 'mini') {
      const targetDate = new Date(date)
      const targetDateString = this.getDateString(targetDate)
      const sourceEvents =
        source === 'mini' ? this.miniCalendarEvents : this.visibleEvents

      return sourceEvents.filter((event) => {
        // For timestamp-based events
        if (typeof event.start === 'number') {
          const eventDate = new Date(event.start)
          return this.getDateString(eventDate) === targetDateString
        }
        // For string-based events (backward compatibility)
        return (
          event.start === targetDateString ||
          event.startDate === targetDateString
        )
      })
    },

    // Drag and Drop Methods
    onPatternDragStart(event, pattern) {
      const selected = this.selectedPatterns.some((p) => p.id === pattern.id)
        ? this.selectedPatterns
        : [pattern]

      this.draggedPattern = pattern

      event.dataTransfer.setData('application/json', JSON.stringify(selected))
      event.dataTransfer.effectAllowed = 'copy'
      event.target.style.opacity = '0.5'
    },

    onPatternDragEnd(event) {
      // Reset visual feedback
      event.target.style.opacity = '1'
      this.draggedPattern = null
      this.dragOverDate = null
    },

    onCalendarDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'

      // Try to determine the date being hovered over
      const targetElement =
        event.target.closest('[data-date]') ||
        event.target.closest('.v-calendar-daily__day') ||
        event.target.closest('.v-calendar-monthly__day')

      if (targetElement) {
        const date = this.getDateFromElement(targetElement, event)
        this.dragOverDate = date
      } else {
        this.dragOverDate = this.focus || new Date().toISOString().substr(0, 10)
      }
    },

    onCalendarDrop(event) {
      event.preventDefault()

      try {
        const patternData = JSON.parse(
          event.dataTransfer.getData('application/json')
        )

        const targetElement =
          event.target.closest('[data-date]') ||
          event.target.closest('.v-calendar-daily__day') ||
          event.target.closest('.v-calendar-monthly__day')

        let dropDate = this.dragOverDate || this.focus

        if (targetElement) {
          dropDate = this.getDateFromElement(targetElement, event) || dropDate
        }

        // Lida com múltiplos padrões
        const patterns = Array.isArray(patternData)
          ? patternData
          : [patternData]
        patterns.forEach((p) => this.createEventFromPattern(p, dropDate))
      } catch (error) {
        console.error('Erro no drop:', error)
        this.$toast.error('Erro ao criar agendamento')
      } finally {
        this.dragOverDate = null
        this.draggedPattern = null
      }
    },
    onCalendarDragLeave(event) {
      // Only clear if we're leaving the calendar area completely
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOverDate = null
      }
    },

    getDateFromElement(element, event) {
      // Try to get date from various calendar element attributes
      if (element.dataset.date) {
        return element.dataset.date
      }

      // For v-calendar elements, try to extract date from classes or other attributes
      const dateMatch = element.className.match(
        /v-calendar-\w*__day-(\d{4}-\d{2}-\d{2})/
      )
      if (dateMatch) {
        return dateMatch[1]
      }

      // Fallback: try to calculate based on calendar type and position
      return this.calculateDateFromPosition(event)
    },

    calculateDateFromPosition(event) {
      // This is a fallback method to estimate date from mouse position
      // Implementation depends on calendar layout
      return this.focus // Return current focus date as fallback
    },

    createEventFromPattern(pattern, dropDate) {
      // Ensure we have a valid date
      const targetDate = new Date(dropDate || this.focus)
      if (isNaN(targetDate.getTime())) {
        this.$toast.error('Data inválida para criar agendamento')
        return
      }

      // Set default time (9 AM to 11 AM)
      const startDateTime = new Date(targetDate)
      startDateTime.setHours(9, 0, 0, 0)

      const endDateTime = new Date(targetDate)
      endDateTime.setHours(11, 0, 0, 0)

      const patterns = [pattern]

      // Pre-fill the event form with the dropped pattern and target date
      this.eventForm = {
        name: '',
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        calibrationPattern: [pattern.id],
        destinationInstitution: null,
        color: pattern.color || '#2196f3',
        details: '',
      }

      // Focus on the target date
      this.focus = this.getDateString(targetDate)

      // Open the event dialog
      this.editingEvent = false
      this.showEventDialog = true

      // Show info message
      const nomes = patterns.map((p) => p.name).join(', ')
      this.$toast.info(`Padrão(s): ${nomes} selecionado(s).`)
    },

    // Calendar Event Drag Methods (based on calendar-example.vue)
    startDrag({ event, timed }) {
      if (event && timed) {
        this.dragEvent = event
        this.dragTime = null
        this.extendOriginal = null
      }
    },

    startTime(tms) {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragTime === null) {
        const start = this.dragEvent.start
        this.dragTime = mouse - start
      } else {
        this.createStart = this.roundTime(mouse)
        this.createEvent = {
          name: `Evento #${this.events.length + 1}`,
          color: this.rndElement([
            '#f44336',
            '#e91e63',
            '#9c27b0',
            '#673ab7',
            '#3f51b5',
            '#2196f3',
            '#03a9f4',
            '#00bcd4',
            '#009688',
            '#4caf50',
            '#8bc34a',
            '#cddc39',
            '#ffeb3b',
            '#ffc107',
            '#ff9800',
            '#ff5722',
          ]),
          start: this.createStart,
          end: this.createStart,
          timed: true,
          // Add default properties for compatibility
          startDateTime: new Date(this.createStart),
          endDateTime: new Date(this.createStart),
          startDate: this.getDateString(new Date(this.createStart)),
          endDate: this.getDateString(new Date(this.createStart)),
          startHour: this.formatTimeFromTimestamp(this.createStart),
          endHour: this.formatTimeFromTimestamp(this.createStart),
          calibrationPattern: null,
          calibrationPatternName: '',
          destinationInstitution: null,
          destinationInstitutionName: '',
          details: 'Evento criado por arrastar e soltar',
        }

        this.events.push(this.createEvent)
      }
    },

    extendBottom(event) {
      this.createEvent = event
      this.createStart = event.start
      this.extendOriginal = event.end
    },

    mouseMove(tms) {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragTime !== null) {
        const start = this.dragEvent.start
        const end = this.dragEvent.end
        const duration = end - start
        const newStartTime = mouse - this.dragTime
        const newStart = this.roundTime(newStartTime)
        const newEnd = newStart + duration

        this.dragEvent.start = newStart
        this.dragEvent.end = newEnd

        // Update helper properties
        this.updateEventTimes(this.dragEvent, newStart, newEnd)
      } else if (this.createEvent && this.createStart !== null) {
        const mouseRounded = this.roundTime(mouse, false)
        const min = Math.min(mouseRounded, this.createStart)
        const max = Math.max(mouseRounded, this.createStart)

        this.createEvent.start = min
        this.createEvent.end = max

        // Update helper properties
        this.updateEventTimes(this.createEvent, min, max)
      }
    },

    endDrag() {
      this.dragTime = null
      this.dragEvent = null
      this.createEvent = null
      this.createStart = null
      this.extendOriginal = null
    },

    cancelDrag() {
      if (this.createEvent) {
        if (this.extendOriginal) {
          this.createEvent.end = this.extendOriginal
          this.updateEventTimes(
            this.createEvent,
            this.createEvent.start,
            this.extendOriginal
          )
        } else {
          const i = this.events.indexOf(this.createEvent)
          if (i !== -1) {
            this.events.splice(i, 1)
          }
        }
      }

      this.createEvent = null
      this.createStart = null
      this.dragTime = null
      this.dragEvent = null
    },

    clickDate(date) {
      if (!this.dragEvent) {
        this.viewDay(date)
      }
    },

    // Helper methods for time calculations
    roundTime(time, down = true) {
      const roundTo = 15 // minutes
      const roundDownTime = roundTo * 60 * 1000

      return down
        ? time - (time % roundDownTime)
        : time + (roundDownTime - (time % roundDownTime))
    },

    toTime(tms) {
      return new Date(
        tms.year,
        tms.month - 1,
        tms.day,
        tms.hour,
        tms.minute
      ).getTime()
    },

    formatTimeFromTimestamp(timestamp) {
      const date = new Date(timestamp)
      return `${date.getHours().toString().padStart(2, '0')}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
    },

    updateEventTimes(event, start, end) {
      const startDate = new Date(start)
      const endDate = new Date(end)

      event.startDateTime = startDate
      event.endDateTime = endDate
      event.startDate = this.getDateString(startDate)
      event.endDate = this.getDateString(endDate)
      event.startHour = this.formatTimeFromTimestamp(start)
      event.endHour = this.formatTimeFromTimestamp(end)
    },

    rndElement(arr) {
      return arr[Math.floor(Math.random() * arr.length)]
    },

    // Métodos de navegação personalizados
    previousPeriod() {
      this.$refs.calendar.prev()
    },

    nextPeriod() {
      this.$refs.calendar.next()
    },

    // Método do date-picker de navegação
    navigateToSelectedDate(date) {
      if (date) {
        this.miniCalendarDate = new Date(date)
        this.getMiniCalendarScheduling(this.miniCalendarDate.getFullYear(), this.miniCalendarDate.getMonth())
      }
    },
  },
}
</script>

<style scoped>
.calendar-app-bar {
  display: flex;
  align-items: center;
}

.custom-tooltip {
  background-color: white !important;
  color: black !important;
  opacity: 1 !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15); /* opcional: sombra suave */
  border: 1px solid #ddd; /* opcional: borda leve */
  padding: 8px;
  font-size: 13px;
  line-height: 1.4;
  font-weight: 400;
  border-radius: 4px;
}

.full-height {
  height: 100%;
}

.card-container {
  position: relative;
  overflow: hidden;
}

.calendar-main-content {
  transition: margin-left 0.3s ease;
}

.drawer-open {
  margin-left: 300px;
}

.calendar-title-bar {
  border-bottom: 1px solid #e0e0e0;
  background: white;
}

.calendar-container {
  height: calc(100% - 70px);
  overflow: auto;
}

.calendar-sidebar {
  border-right: 1px solid #e0e0e0;
  background: white;
  z-index: 5;
}

.mini-calendar {
  width: 100%;
}

.mini-calendar-day {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.mini-calendar-day.has-events {
  background-color: rgba(25, 118, 210, 0.1);
  font-weight: 500;
}

.event-content {
  width: 100%;
  height: 100%;
  padding: 2px 4px;
  display: flex;
  flex-direction: column;
}

.event-title {
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-time {
  font-size: 10px;
  opacity: 0.8;
  line-height: 1.2;
}

/* Custom calendar styling */
::v-deep .v-calendar-daily_head-day-label {
  font-size: 0.875rem;
  font-weight: 500;
}

::v-deep .v-calendar-weekly__week {
  min-height: 150px;
}

::v-deep .v-calendar-monthly__day {
  position: relative;
}

::v-deep .v-calendar-monthly__day-month {
  font-size: 0.875rem;
  font-weight: 500;
}

::v-deep .v-event {
  border-radius: 4px;
  font-size: 12px;
  margin: 1px 0;
  padding: 2px 4px;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
}

::v-deep .v-navigation-drawer__content {
  overflow-y: auto;
}

/* Mini Calendar Styles */
.mini-calendar-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.mini-calendar-header {
  padding: 8px 4px;
}

.mini-calendar-title {
  color: #1976d2;
  cursor: pointer;
  flex: 1;
  text-align: center;
}

.calendar-nav-btn {
  color: #5f6368;
}

.calendar-nav-btn:hover {
  background-color: rgba(95, 99, 104, 0.1);
}

.mini-calendar-grid {
  width: 100%;
}

.mini-calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f8f9fa;
  border-top: 1px solid #e8eaed;
  border-bottom: 1px solid #e8eaed;
}

.mini-calendar-weekday {
  padding: 8px 4px;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  color: #5f6368;
}

.mini-calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.mini-calendar-day {
  position: relative;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.mini-calendar-day:hover {
  background-color: #f1f3f4;
}

.mini-calendar-day.other-month {
  color: #9aa0a6;
}

.mini-calendar-day.today {
  background-color: #1976d2;
  color: white;
  border-radius: 50%;
}

.mini-calendar-day.today:hover {
  background-color: #1565c0;
}

.mini-calendar-day.selected {
  background-color: #e3f2fd;
  border-color: #1976d2;
}

.mini-calendar-day.selected.today {
  background-color: #1976d2;
  color: white;
}

.day-number {
  font-size: 13px;
  font-weight: 400;
  position: relative;
  z-index: 2;
}

.event-indicators {
  position: absolute;
  bottom: 1px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1px;
  align-items: center;
}

.event-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #1976d2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.event-dot:hover {
  transform: scale(1.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.event-dot-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: inherit;
}

.more-events {
  font-size: 8px;
  color: #5f6368;
  margin-left: 1px;
  cursor: pointer;
  padding: 1px 2px;
  border-radius: 2px;
  transition: all 0.2s ease;
}

.more-events:hover {
  background-color: rgba(95, 99, 104, 0.1);
  color: #1976d2;
}

.mini-calendar-day.has-events .day-number {
  font-weight: 500;
}

/* Patterns Section Styles */
.patterns-section {
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: 300px;
  max-height: calc(100vh - 450px);
}

.patterns-list-container {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 550px);
  min-height: 200px;
}

.patterns-list {
  padding: 0;
}

.pattern-item {
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
}

.pattern-item:hover {
  background-color: #f8f9fa;
}

.pattern-selected {
  background-color: #e3f2fd !important;
  border-left: 4px solid #1976d2;
}

.pattern-selected:hover {
  background-color: #e3f2fd !important;
}

.pattern-dragging {
  opacity: 0.5;
  transform: scale(0.95);
  transition: all 0.2s ease;
}

.drag-handle {
  color: #9e9e9e;
  cursor: grab;
}

.drag-handle:hover {
  color: #1976d2;
}

.pattern-item[draggable='true'] {
  cursor: grab;
}

.pattern-item[draggable='true']:active {
  cursor: grabbing;
}

/* Custom scrollbar for patterns list */
.patterns-list-container::-webkit-scrollbar {
  width: 6px;
}

.patterns-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.patterns-list-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.patterns-list-container::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .mini-calendar-day {
    height: 28px;
  }

  .day-number {
    font-size: 12px;
  }

  .patterns-section {
    max-height: calc(100vh - 400px);
    min-height: 250px;
  }

  .patterns-list-container {
    max-height: calc(100vh - 500px);
    min-height: 150px;
  }
}

/* Zoom adjustments */
@media (min-resolution: 120dpi) {
  .patterns-section {
    max-height: calc(100vh - 400px);
  }

  .patterns-list-container {
    max-height: calc(100vh - 500px);
  }
}

/* Drag and Drop Calendar Styles */
.calendar-drop-zone {
  position: relative;
  transition: all 0.3s ease;
}

.calendar-drop-zone.drag-over {
  background-color: rgba(25, 118, 210, 0.05);
  border: 2px dashed #1976d2;
  border-radius: 8px;
}

.calendar-drop-zone.drag-over::before {
  content: 'Solte o padrão aqui para abrir o formulário de agendamento';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(25, 118, 210, 0.9);
  color: white;
  padding: 12px 24px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Calendar day hover effect during drag */
::v-deep .v-calendar-monthly__day:hover,
::v-deep .v-calendar-daily__day:hover {
  background-color: rgba(25, 118, 210, 0.1) !important;
}

/* Event Drag and Drop Styles */
.v-event-draggable {
  padding-left: 6px;
}

.v-event-timed {
  user-select: none;
  -webkit-user-select: none;
}

.v-event-drag-bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  height: 4px;
  cursor: ns-resize;

  &::after {
    display: none;
    position: absolute;
    left: 50%;
    height: 4px;
    border-top: 1px solid white;
    border-bottom: 1px solid white;
    width: 16px;
    margin-left: -8px;
    opacity: 0.8;
    content: '';
  }

  &:hover::after {
    display: block;
  }
}

/* Calendar time slot hover effect */
::v-deep .v-calendar-timed__time-content:hover {
  background-color: rgba(25, 118, 210, 0.05);
}

/* Prevent text selection during dragging */
.v-calendar-main {
  user-select: none;
}
</style>
